import React, { useEffect, useMemo, useState } from 'react';
import { X, Search } from 'lucide-react';
import { staffMemberDocuments } from '../../services/staffDocumentService';
import { templateMeta, templateTypeLabel } from './templateTypeMeta';

import { RubyGemMark } from '../RubyGemMark';
// Port of platform-app components/team/AssignWorkflowModal.tsx for the
// tenant-less staff-service (RUBY light chrome). `tenantId` is accepted for
// prop-contract compatibility but ignored — staff-service has no tenants.

interface AssignWorkflowModalProps {
  /** Ignored (tenant-less staff-service); kept so page ports stay mechanical. */
  tenantId?: string;
  employee: { id: string; name: string };
  workflows: { id: string; name: string; documents?: { templateId: string }[] }[]; // full OnboardingTemplate list
  templates: { id: string; templateType?: string }[]; // for resolving each step's type
  onAssign: (workflowId: string) => Promise<void>;
  onClose: () => void;
}

export default function AssignWorkflowModal({
  employee,
  workflows,
  templates,
  onAssign,
  onClose,
}: AssignWorkflowModalProps) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());

  // Resolve templateId -> templateType
  const typeById = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    for (const t of templates) map[t.id] = t.templateType;
    return map;
  }, [templates]);

  // Fetch already-assigned workflows (best-effort; on error treat as none)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await staffMemberDocuments.getOnboardingStatus(employee.id);
        if (cancelled) return;
        const ids = new Set<string>(
          (status?.workflows || [])
            .map((w) => w.onboardingTemplateId)
            .filter((id): id is string => Boolean(id))
        );
        setAssignedIds(ids);
      } catch {
        if (!cancelled) setAssignedIds(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  // Derive the distinct type-mix (in canonical order) for a workflow
  const typeMix = (docs?: { templateId: string }[]): string[] => {
    if (!docs || docs.length === 0) return [];
    const order = ['acknowledgment', 'fillable_doc', 'form', 'sheet', 'upload'];
    const seen = new Set<string>();
    for (const d of docs) seen.add(typeById[d.templateId] || 'upload');
    return order.filter((t) => seen.has(t));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workflows;
    return workflows.filter((w) => w.name.toLowerCase().includes(q));
  }, [workflows, search]);

  const selectedName = workflows.find((w) => w.id === chosen)?.name;

  const handleAssign = async () => {
    if (!chosen) return;
    setError(null);
    setAssigning(true);
    try {
      await onAssign(chosen);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to assign workflow. Please try again.');
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl border border-stone-200 shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-stone-900">Assign Workflow</h2>
            <p className="text-[11px] text-stone-500">to {employee.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={assigning}
            className="p-1 text-stone-400 hover:text-stone-700 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-2 overflow-y-auto">
          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-200 mb-1">
            <Search size={14} className="text-stone-400 flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search workflows…"
              className="w-full text-sm bg-transparent text-stone-900 placeholder-stone-400 outline-none"
            />
          </div>

          {/* Cards */}
          {filtered.length === 0 ? (
            <p className="text-sm text-stone-500 px-1 py-4 text-center">No workflows found.</p>
          ) : (
            filtered.map((w) => {
              const selected = chosen === w.id;
              const isAssigned = assignedIds.has(w.id);
              const stepCount = w.documents?.length || 0;
              const mix = typeMix(w.documents);
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setChosen(w.id)}
                  className={`w-full text-left rounded-xl p-3 transition-colors ${
                    selected
                      ? 'border-2 border-admin-primary bg-admin-primary/10'
                      : 'border border-stone-200 hover:border-stone-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-stone-900 flex-1">{w.name}</span>
                    {isAssigned && (
                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Already assigned
                      </span>
                    )}
                    {selected && <span className="text-admin-primary leading-none">●</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-[11px] text-stone-500">{stepCount} steps ·</span>
                    {mix.map((t) => {
                      const meta = templateMeta(t);
                      return (
                        <span
                          key={t}
                          className={`text-[11px] font-medium px-1 py-0.5 rounded ${meta.badge}`}
                        >
                          {templateTypeLabel(t)}
                        </span>
                      );
                    })}
                    {isAssigned && (
                      <span className="text-[11px] text-stone-400">· assigning starts a fresh round</span>
                    )}
                  </div>
                </button>
              );
            })
          )}

          {error && <p className="text-[12px] text-red-600 px-1">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-stone-200">
          <button
            type="button"
            onClick={onClose}
            disabled={assigning}
            className="px-3 py-2 text-sm text-stone-500 hover:text-stone-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={!chosen || assigning}
            className="inline-flex items-center gap-1.5 bg-admin-primary hover:bg-admin-secondary text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {assigning && <RubyGemMark size={14} drawing color="currentColor" />}
            {assigning ? 'Assigning…' : selectedName ? `Assign “${selectedName}”` : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}
